import { formatCurrency, pluralize } from "@sellar-io/common";
import { twMerge } from "tailwind-merge";
import { Card, CardContent, CardHeader, Icon } from "@sellar-io/components";
import { UnifiedCheckoutCardPaymentOptions } from "./UnifiedCheckoutCardPaymentOptions";

import { UnifiedCheckoutPaymentOption } from "./UnifiedCheckoutPaymentOption";
import sellarLogoNew from "../../../images/sellar-logo-new.png";
import dayjs from "dayjs";
import { TwoSellarCreditVerificationRequiredAlert } from "../../../components/twoSellarCreditVerificationRequiredAlert/TwoSellarCreditVerificationRequiredAlert";
import type {
  CheckoutInvoiceMethod,
  CheckoutPaymentType,
} from "../../stockList/checkoutV2/lib/types";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { useCreditTermsText } from "../../../components/paymentTypeIndicator/useCreditTermsText";
import { useConnectionForCheckout } from "../../stockList/checkoutV2/lib/CheckoutProvider.shared";

type SelectedPaymentMethod = {
  type: CheckoutPaymentType | "";
  paymentMethodId?: string;
  brand?: string;
  last4?: string;
  paymentLinkRequested?: boolean;
};

type SupplierPaymentCardProps = {
  supplierInfo?: {
    supplier: {
      id: number;
      Business: {
        name: string;
        logoUrl: string | null;
      };
      stripeAccountId?: string | null;
    };
  };
  itemCount: number;
  amount: number;
  currency: "gbp" | "sgd";
  vatPercentage: number;
  isActive: boolean;
  selectedPaymentMethod: SelectedPaymentMethod;
  onActivate?: () => void;
  availablePaymentTypes?: CheckoutPaymentType[];
  savedPaymentMethods?: Array<{
    stripePaymentMethodId: string;
    brand: string;
    last4Digits: string;
  }>;
  sellarCreditVerificationRequired?: boolean;
  sellarCreditBalance?: number;
  canUserViewSellarCreditBalance?: boolean;
  invoiceMethod?: CheckoutInvoiceMethod;
  invoiceEmails?: string[];
  onOpenPaymentLinkSettings?: () => void;
  onSelectPaymentMethod?: (selection: {
    paymentType: CheckoutPaymentType | "";
    paymentMethodId?: string;
    invoiceMethod?: CheckoutInvoiceMethod;
    paymentLinkRequested?: boolean;
  }) => void;
};

export const SupplierPaymentCard = ({
  supplierInfo,
  itemCount,
  amount,
  currency,
  vatPercentage,
  isActive,
  selectedPaymentMethod,
  onActivate,
  availablePaymentTypes = [],
  savedPaymentMethods = [],
  sellarCreditVerificationRequired = false,
  sellarCreditBalance = 0,
  canUserViewSellarCreditBalance = false,
  invoiceMethod = "manual-invoice",
  invoiceEmails = [],
  onSelectPaymentMethod,
  onOpenPaymentLinkSettings,
}: SupplierPaymentCardProps) => {
  const supplierId = supplierInfo?.supplier.id ?? 0;
  const { data: connection } = useConnectionForCheckout({ supplierId });
  const supplierCreditTerms = useCreditTermsText({
    connection,
    includePaymentType: false,
  });
  const { isActive: isFeatureActive } = useRemoteFeatureFlags();

  if (!supplierInfo) {
    return null;
  }
  const supplierName = supplierInfo.supplier.Business.name;
  const logoUrl = supplierInfo.supplier.Business.logoUrl;

  return (
    <Card
      className={twMerge(
        "rounded-2xl border border-zinc-200 bg-white shadow-sm transition-all",
      )}
    >
      {/* Header */}
      <CardHeader
        onClick={onActivate}
        className={twMerge(
          "grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-t-2xl bg-transparent p-4 sm:flex sm:justify-between sm:p-5",
          onActivate && "cursor-pointer hover:bg-zinc-50",
          !isActive && "rounded-b-2xl border-0",
        )}
      >
        <div className="contents sm:flex sm:min-w-0 sm:items-center sm:gap-4">
          <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full border border-zinc-100 bg-zinc-50">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={supplierName}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-zinc-400">
                <Icon icon="STOREFRONT_OUTLINE" size={0.6} />
              </div>
            )}
          </div>

          <div className="flex min-w-0 flex-col">
            <span className="truncate font-semibold">{supplierName}</span>
            <span className="text-zinc-600">
              {itemCount} {pluralize("item", itemCount)}
            </span>
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end">
          <div className="flex items-center gap-2">
            <span className="font-semibold">
              {formatCurrency(amount, currency)}
            </span>
          </div>
          {vatPercentage > 0 && <span className="text-zinc-600">inc. VAT</span>}
        </div>
      </CardHeader>

      {isActive && (
        <CardContent className="flex flex-col gap-2 p-4">
          <span className="text-sm">Select payment method</span>
          <div className="flex flex-col gap-2">
            <UnifiedCheckoutPaymentOption
              visible={availablePaymentTypes.includes("sellar-credit")}
              selected={selectedPaymentMethod.type === "sellar-credit"}
              trigger={
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1 text-sm">
                  <span className="font-semibold">Use my</span>
                  <img src={sellarLogoNew} className="h-4 w-4" alt="" />
                  <span className="font-semibold text-[#C637CB]">
                    Sellar Credit
                  </span>
                  <span>account</span>
                  {selectedPaymentMethod.type !== "sellar-credit" &&
                    canUserViewSellarCreditBalance && (
                      <span className="text-zinc-500">
                        · Balance available:{" "}
                        {formatCurrency(sellarCreditBalance, currency)}
                      </span>
                    )}
                </div>
              }
              onSelect={() =>
                onSelectPaymentMethod?.({ paymentType: "sellar-credit" })
              }
            >
              {sellarCreditVerificationRequired && (
                <TwoSellarCreditVerificationRequiredAlert
                  fontSize="text-base"
                  buttonFontSize="text-sm"
                  message="To keep using Sellar Credit, you need to complete verification with our new credit provider, Two Inc."
                />
              )}
              <p className="text-zinc-600">
                Put your order on your Sellar Credit tab.
                {canUserViewSellarCreditBalance && (
                  <>
                    {" "}
                    Balance available:{" "}
                    {formatCurrency(sellarCreditBalance, currency)}
                  </>
                )}
              </p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg bg-[#FAF0FF] p-3">
                <span className="flex items-center gap-1">
                  <Icon
                    icon="CALENDAR_CLOCK_OUTLINE"
                    size={0.7}
                    className="text-[#C637CB]"
                  />
                  Pay in{" "}
                  {dayjs().endOf("month").add(30, "days").diff(dayjs(), "days")}{" "}
                  days
                </span>
                <span className="flex items-center gap-1">
                  <Icon
                    icon="NOTE_OFF_OUTLINE"
                    size={0.7}
                    className="text-[#C637CB]"
                  />
                  Less admin
                </span>
                <span className="flex items-center gap-1">
                  <Icon
                    icon="TRUCK_OUTLINE"
                    size={0.7}
                    className="text-[#C637CB]"
                  />
                  Supplier gets paid on dispatch
                </span>
              </div>
            </UnifiedCheckoutPaymentOption>
            <UnifiedCheckoutPaymentOption
              visible={availablePaymentTypes.includes("credit-account")}
              selected={selectedPaymentMethod.type === "credit-account"}
              trigger={
                <div className="flex flex-wrap items-center gap-1 text-sm">
                  <span className="font-semibold">
                    Use my credit account with {supplierName}
                  </span>
                  {selectedPaymentMethod.type !== "credit-account" &&
                    supplierCreditTerms && (
                      <span className="text-zinc-600">
                        · {supplierCreditTerms}
                      </span>
                    )}
                </div>
              }
              onSelect={() =>
                onSelectPaymentMethod?.({
                  paymentType: "credit-account",
                  invoiceMethod,
                })
              }
            >
              <p className="text-zinc-600">
                Invoice issued on finalisation of order.
                {supplierCreditTerms && (
                  <>
                    {" "}
                    Credit terms:{" "}
                    <strong className="font-semibold">
                      {supplierCreditTerms}
                    </strong>
                  </>
                )}
              </p>
              {selectedPaymentMethod.paymentLinkRequested && (
                <div className="rounded-lg bg-[#FAF0FF] p-3 text-sm">
                  <p className="font-semibold text-zinc-800">
                    Send card payment link to
                  </p>
                  <p className="text-zinc-600">{invoiceEmails.join(", ")}</p>
                </div>
              )}
              {onOpenPaymentLinkSettings &&
                (!isFeatureActive("checkoutPaymentLinkRequest") ||
                  supplierInfo.supplier.stripeAccountId) && (
                  <button
                    type="button"
                    className="cursor-pointer self-start text-left text-sm font-semibold text-purple-700"
                    onClick={onOpenPaymentLinkSettings}
                  >
                    Payment link settings ↗
                  </button>
                )}
            </UnifiedCheckoutPaymentOption>
            <UnifiedCheckoutPaymentOption
              visible={
                availablePaymentTypes.includes("existing-card-payment") ||
                availablePaymentTypes.includes("new-card-payment")
              }
              selected={
                selectedPaymentMethod.type === "existing-card-payment" ||
                selectedPaymentMethod.type === "new-card-payment"
              }
              trigger={
                <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-sm">
                  <div className="flex items-center gap-1">
                    <span className="font-semibold">Pay now by card</span>
                    {!selectedPaymentMethod.type.includes("card") &&
                      savedPaymentMethods.length > 0 && (
                        <span className="text-zinc-500">
                          · {savedPaymentMethods.length} saved{" "}
                          {pluralize("card", savedPaymentMethods.length)}
                        </span>
                      )}
                  </div>
                  {selectedPaymentMethod.type.includes("card") && (
                    <span className="text-zinc-500">
                      Instant payment with no need for any back and forth
                    </span>
                  )}
                </div>
              }
              onSelect={() =>
                onSelectPaymentMethod?.({
                  paymentType: savedPaymentMethods[0]
                    ? "existing-card-payment"
                    : "new-card-payment",
                  paymentMethodId:
                    savedPaymentMethods[0]?.stripePaymentMethodId,
                })
              }
            >
              <div>
                <UnifiedCheckoutCardPaymentOptions
                  existingCardPaymentMethods={savedPaymentMethods}
                  allowNewCardPayment={availablePaymentTypes.includes(
                    "new-card-payment",
                  )}
                  activePayment={
                    selectedPaymentMethod.type === "existing-card-payment"
                      ? {
                          type: "existing-card",
                          paymentMethodId:
                            selectedPaymentMethod.paymentMethodId ?? "",
                        }
                      : { type: "new-card" }
                  }
                  onSelectPaymentMethod={(payment) =>
                    onSelectPaymentMethod?.({
                      paymentType:
                        payment.type === "new-card"
                          ? "new-card-payment"
                          : "existing-card-payment",
                      paymentMethodId:
                        payment.type === "existing-card"
                          ? payment.paymentMethodId
                          : undefined,
                    })
                  }
                />
              </div>
            </UnifiedCheckoutPaymentOption>
          </div>
        </CardContent>
      )}
    </Card>
  );
};
